import { useState, useEffect } from "react";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useAutoRegistration } from "@/hooks/useAutoRegistration";
import { supabase } from "@/integrations/supabase/client";
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock,
  Send
} from "lucide-react";
import { SEOHead } from "@/components/seo";
import { generateContactPageSchema, generateLocalBusinessSchema } from "@/components/seo/SchemaMarkup";
import ContactMap from "@/components/contact/ContactMap";
import { trackPixelLead } from "@/lib/retargeting";
import { trackGA4Event } from "@/lib/ga4";
import { useAgentBranding } from "@/hooks/useAgentBranding";

const Contact = () => {
  useEffect(() => {
    document.title = "Contact Prime Realty Partners | South Jersey Real Estate";
  }, []);

  const { toast } = useToast();
  const { autoRegisterLead } = useAutoRegistration();
  const { agentSlug } = useAgentBranding();
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    tcpa_consent: false,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setSubmitting(true);
    try {
      // Send to Follow Up Boss via edge function
      const { error: fubError } = await supabase.functions.invoke("submit-lead", {
        body: {
          event_type: "contact_form",
          lead_data: {
            name: formData.name,
            email: formData.email,
            phone: formData.phone || "",
            message: formData.message,
            tcpa_consent: formData.tcpa_consent,
            consent_timestamp: formData.tcpa_consent ? new Date().toISOString() : null,
          },
          metadata: {
            ...(agentSlug && { assign_to_agent: true, agent_slug: agentSlug }),
          },
        },
      });

      if (fubError) throw fubError;

      toast({
        title: "Message Sent!",
        description: "Thank you for reaching out. We'll get back to you shortly.",
      });

      // Track Lead conversion for retargeting pixels
      trackPixelLead({
        formType: "contact_form",
        contentName: "Contact Page Inquiry",
      });

      trackGA4Event('form_submit', { event_category: 'lead_action', form_name: 'contact_form', form_page: window.location.pathname });

      // Auto-register the lead
      await autoRegisterLead({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
      });

      setFormData({
        name: "",
        email: "",
        phone: "",
        message: "",
        tcpa_consent: false,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: "Office Address",
      details: ["31 Walnut Ave", "Bellmawr, NJ 08031"],
    },
    {
      icon: Phone,
      title: "Phone",
      details: ["(856) 210-3000"],
    },
    {
      icon: Mail,
      title: "Email",
      details: ["info@prpsj.com"],
    },
    {
      icon: Clock,
      title: "Office Hours",
      details: ["Monday - Friday: 8:30 AM - 6 PM", "Saturday - Sunday: 10 AM - 5 PM"],
    },
  ];

  const contactPageSchema = generateContactPageSchema();
  const localBusinessSchema = generateLocalBusinessSchema();

  return (
    <Layout>
      <SEOHead
        title="Contact Prime Realty Partners | South Jersey Real Estate"
        description="Get in touch with Prime Realty Partners. Call, email, or visit our South Jersey office. We're here to help with buying, selling, or investing in real estate."
        canonicalUrl="https://www.prpsj.com/contact"
        keywords="contact Prime Realty Partners, Bellmawr NJ realtor, New Jersey real estate office, real estate agent contact"
        geoPlacename="Bellmawr, NJ"
        schema={[contactPageSchema, localBusinessSchema]}
        breadcrumbs={[{ name: "Home", url: "https://www.prpsj.com" }, { name: "Contact", url: "https://www.prpsj.com/contact" }]}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary/10 to-primary/5 py-16">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
              Contact Prime Realty Partners
            </h1>
            <p className="text-lg text-muted-foreground mb-4">
              Contact Prime Realty Partners — we're here to help with all your South Jersey real estate needs. Whether you're buying your first home, selling a property, or exploring investment opportunities across Burlington, Camden, and Gloucester counties, our team is ready to assist. Reach out by phone, email, or fill out the form below.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16">
        <div className="container">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Send className="h-5 w-5 text-primary" />
                    Send Us a Message
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="John Smith"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email Address *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="john@example.com"
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="(555) 123-4567"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Your Message *</Label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        placeholder="How can we help you?"
                        rows={5}
                        required
                      />
                    </div>

                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="consent"
                        checked={formData.tcpa_consent}
                        onCheckedChange={(checked) => 
                          setFormData({ ...formData, tcpa_consent: checked as boolean })
                        }
                      />
                      <Label htmlFor="consent" className="text-xs text-muted-foreground leading-tight">
                        By submitting this form, you agree to be contacted by MarChris Ventures LLC dba Prime Realty Partners via call, email, and text regarding real estate and mortgage services, including property updates, appointment reminders, and transactional communications. Message frequency may vary. Message and data rates may apply. You may opt out at any time by replying STOP, or reply HELP for assistance.
                        <br />
                        Consent is not a condition of purchase.
                        <br />
                        <a href="/privacy" className="underline hover:text-primary">Privacy Policy</a>
                      </Label>
                    </div>

                    <Button type="submit" size="lg" className="w-full" disabled={submitting}>
                      {submitting ? "Sending..." : "Send Message"}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Contact Info Sidebar */}
            <div className="space-y-6">
              {contactInfo.map((info, index) => (
                <Card key={index}>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <info.icon className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold mb-1">{info.title}</h3>
                        {info.details.map((detail, i) => (
                          <p key={i} className="text-sm text-muted-foreground">
                            {detail}
                          </p>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Map Section */}
      <section className="py-8 bg-muted/30">
        <div className="container">
          <h2 className="font-heading text-2xl font-bold mb-6 text-center">
            Find Our Office
          </h2>
          <div className="rounded-xl overflow-hidden shadow-lg">
            <ContactMap />
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Contact;
